import { AxiosResponse } from "axios";
import HttpService from "./interface/HttpService";
import { BvnDetailsResponse, BvnInitializationResponse, BvnVerificationResponse, CustomerRegistrationPersonalInformation } from "../stores/slices/customer-registration-slice";
import { IdentityInformation } from "../model/IndentityInformation";
import { CreditEngineScore } from "../model/customer/customer";
import { FileFromObjectUrl } from "../model/common/file-from-object-url";


class CustomerService {
    private instance: HttpService;
    private controllerName: string = 'customer';

    constructor() {
        this.instance = new HttpService(this.controllerName);
    }

    public async initiateVerifyPhoneNumber({
        phoneNumber,
    }: {
        phoneNumber: string,
    }): Promise<AxiosResponse<void>> {
        return this.instance.post('registration/initiate-phone-verification',
            {
                phoneNumber: phoneNumber,
            }).catch((error) => {
                throw new Error(error.response.data.title);
            });
    }

    public async validatePhoneNumber({
        token
    }: {
        token: string
    }): Promise<AxiosResponse<void>> {
        return this.instance.post('registration/validate-phone',
            {
                otp: token
            }).catch((error) => {
                throw new Error(error.response.data.title);
            });
    }

    public async updatePersonalInformation({
        personalInformation
    }: {
        personalInformation: CustomerRegistrationPersonalInformation
    }): Promise<AxiosResponse<void>> {
        const form = new FormData();
        form.append('firstName', personalInformation.firstName!);
        form.append('lastName', personalInformation.lastName!);
        form.append('address', personalInformation.address!);
        form.append('state', personalInformation.state!);
        form.append('city', personalInformation.city!);
        form.append('meterNumber', personalInformation.meterNumber!);
        form.append('electricityDistributionCompany', personalInformation.electricityDistributionCompany!);

        const addressProof = personalInformation.addressProofFile
        
        if(addressProof?.url) {
            let blob = await fetch(addressProof.url).then(r => r.blob());

            let file = new File([blob], addressProof.name, { type: blob.type });

            form.append('addressProof', file);
        }

        return this.instance.post('registration/update-personal-information',
            form,
            { headers: { 'Content-Type': 'multipart/form-data' } }).catch((error) => {
                throw new Error(error.response.data.title);
            });
    }

    public async updateEmploymentInformation({
        employmentInformation
    }: {
        employmentInformation: {
            companyName: string,
            jobTitle: string,
            joinDate: Date,
            employmentContract: FileFromObjectUrl,
            employmentType: string,
            linkedInPage?: string
        }
    }): Promise<AxiosResponse<void>> {
        const form = new FormData();

        form.append('companyName', employmentInformation.companyName);
        form.append('jobTitle', employmentInformation.jobTitle);
        form.append('joinDate', employmentInformation.joinDate.toISOString());
        form.append('employmentType', employmentInformation.employmentType);
        employmentInformation.linkedInPage && form.append('linkedInPage', employmentInformation.linkedInPage);

        const employmentContract = employmentInformation.employmentContract
        
        if(employmentContract?.url) {
            let blob = await fetch(employmentContract.url).then(r => r.blob());

            let file = new File([blob], employmentContract.name, { type: blob.type });

            form.append('employmentContract', file);
        }

        return this.instance.post('registration/update-employment-information',
            form,
            { headers: { 'Content-Type': 'multipart/form-data' } }).catch((error) => {
                throw new Error(error.response.data.title);
            });
    }

    public async updateIdentityInformation({
        identityPhoto,
        identityType,
        identityNumber,
    }: IdentityInformation): Promise<AxiosResponse<void>> {
        const form = new FormData();

        console.log(identityType)
        form.append('identityType', identityType);
        form.append('identityNumber', identityNumber);

        if(identityPhoto?.url) {
            let blob = await fetch(identityPhoto.url).then(r => r.blob());

            let file = new File([blob], identityPhoto.name, { type: blob.type });

            form.append('identityPhoto', file);
        }

        return this.instance.post('registration/update-identity-information',
            form,
            { headers: { 'Content-Type': 'multipart/form-data' } }).catch((error) => {
                throw new Error(error.response.data.title);
            });
    }

    public async initializeBvn({
        bvn,
        scope
    }: {
        bvn: string,
        scope: string,
    }): Promise<AxiosResponse<BvnInitializationResponse>> {
        return this.instance.post('bvn/initiate',
            {
                bvn: bvn,
                scope: scope
            }).catch((error) => {
                throw new Error(error.response.data.title);
            });
    }

    public async verifyBvn({
        method,
        phoneNumber,
        sessionId
    }: {
        method: string,
        phoneNumber: string,
        sessionId: string
    }): Promise<AxiosResponse<BvnVerificationResponse>> {
        return this.instance.post('bvn/verify',
            {
                method: method,
                sessionId: sessionId,
                phoneNumber: phoneNumber
            }).catch((error) => {
                throw new Error(error.response.data.title);
            });
    }

    public async detailsBvn({
        otp,
        sessionId
    }: {
        otp: string,
        sessionId: string
    }): Promise<AxiosResponse<BvnDetailsResponse>> {
        return this.instance.post('bvn/details',
            {
                otp: otp,
                sessionId: sessionId
            }).catch((error) => {
                throw new Error(error.response.data.title);
            });
    }

    public async updateMono({
        monoTemporaryToken
    }: {
        monoTemporaryToken: string
    }): Promise<AxiosResponse<void>> {
        return this.instance.post('registration/save-mono',
            {
                monoTemporaryToken: monoTemporaryToken,
            }).catch((error) => {
                throw new Error(error.response.data.title);
            });
    }

    public async getCreditEngineScore({
        customerId,
        from,
    }: {
        customerId: string,
        from: string
    }): Promise<AxiosResponse<CreditEngineScore>> {
        return this.instance.get('credit-engine',
            {
                params: {
                    customerId: customerId,
                    from: from
                }
            }).catch((error) => {
                throw new Error(error.response.data.title);
            });
    }
}

const customerService = new CustomerService();

export default customerService;
